import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import ServiceCard from './components/ServiceCard';
import ServiceModal from './components/ServiceModal';
import ServiceComparison from './components/ServiceComparison';
import ProcessTimeline from './components/ProcessTimeline';
import ContactForm from './components/ContactForm';
import Footer from 'pages/home/components/Footer';
import { motion, useInView } from 'framer-motion'; // Framer Motion import

// Animation Variants for a staggered effect (Kept for other sections)
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

// --- NEW/MODIFIED: Custom Card Variants function based on index for direction ---
const getCardVariants = (index) => {
  // Check if the card is in the left column (even index) or right column (odd index)
  const isLeftColumn = index % 2 === 0;

  return {
    hidden: {
      opacity: 0,
      // Slide from left (-100) for even indices, from right (100) for odd indices
      x: isLeftColumn ? -100 : 100
    },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
        delay: 0.05, // Small delay for visual effect, stagger handled by individual component mounting
      },
    },
  };
};

// --- NEW: Component to handle per-card useInView logic ---
const AnimatedServiceCard = ({ service, index, onServiceClick, onComparisonToggle, isInComparison, comparisonDisabled }) => {
  const ref = useRef(null);
  // Trigger animation when 20% of the item is visible
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <motion.div
      ref={ref}
      // Dynamically assign variants based on index
      variants={getCardVariants(index)}
      initial="hidden"
      // Only animate if the specific card is in view
      animate={isInView ? "show" : "hidden"}
      // Optional: whileHover for interactivity
      whileHover={{ scale: 1.02 }}
    >
      <ServiceCard
        service={service}
        onServiceClick={onServiceClick}
        onComparisonToggle={onComparisonToggle}
        isInComparison={isInComparison}
        comparisonDisabled={comparisonDisabled}
      />
    </motion.div>
  );
};
// --------------------------------------------------------------------

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [comparisonServices, setComparisonServices] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [activeSection, setActiveSection] = useState('services');

  // Refs for Scroll-Based Animations
  const servicesRef = useRef(null);
  const isServicesInView = useInView(servicesRef, { once: true, amount: 0.2 });

  const processRef = useRef(null);
  const isProcessInView = useInView(processRef, { once: true, amount: 0.2 });

  const metricsRef = useRef(null);
  const isMetricsInView = useInView(metricsRef, { once: true, amount: 0.2 });

  const contactRef = useRef(null);
  const isContactInView = useInView(contactRef, { once: true, amount: 0.2 });

  // Custom Card Variants function based on index
  /* Removed: Moved to the top and slightly modified into getCardVariants(index) */

  const services = [
    {
      id: 'web-development',
      icon: 'Globe',
      title: 'Web Development',
      description: 'Custom web applications built with modern technologies for optimal performance and user experience.',
      shortDesc: 'Modern web applications with cutting-edge technologies',
      benefits: ['Responsive Design', 'SEO Optimized', 'Fast Loading', 'Cross-browser Compatible'],
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS'],
      pricing: {
        basic: '$2,500 - $5,000',
        standard: '$5,000 - $15,000',
        premium: '$15,000 - $50,000'
      },
      caseStudy: {
        title: 'E-commerce Platform for Fashion Retailer',
        challenge: 'Client needed a scalable e-commerce solution to handle 10,000+ daily visitors with real-time inventory management.',
        solution: 'Built a React-based platform with Node.js backend, integrated payment gateways, and implemented advanced search functionality.',
        results: '300% increase in online sales, 40% reduction in page load time, 95% customer satisfaction rate.'
      },
      process: ['Requirements Analysis', 'UI/UX Design', 'Development', 'Testing', 'Deployment', 'Maintenance']
    },
    {
      id: 'mobile-development',
      icon: 'Smartphone',
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications for iOS and Android with seamless user experiences.',
      shortDesc: 'Native and cross-platform mobile solutions',
      benefits: ['Cross-platform Compatibility', 'Native Performance', 'Offline Functionality', 'Push Notifications'],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin'],
      pricing: {
        basic: '$5,000 - $15,000',
        standard: '$15,000 - $40,000',
        premium: '$40,000 - $100,000'
      },
      caseStudy: {
        title: 'Healthcare Management App',
        challenge: 'Healthcare provider needed a HIPAA-compliant mobile app for patient management and telemedicine.',
        solution: 'Developed a React Native app with secure authentication, video calling, and encrypted data storage.',
        results: '500+ healthcare providers onboarded, 99.9% uptime, full HIPAA compliance achieved.'
      },
      process: ['Market Research', 'Wireframing', 'Prototype', 'Development', 'Testing', 'App Store Submission']
    },
    {
      id: 'cloud-solutions',
      icon: 'Cloud',
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure and migration services to optimize your business operations.',
      shortDesc: 'Scalable cloud infrastructure and migration services',
      benefits: ['Cost Optimization', 'Auto Scaling', 'High Availability', '24/7 Monitoring'],
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Docker'],
      pricing: {
        basic: '$1,000 - $3,000/month',
        standard: '$3,000 - $8,000/month',
        premium: '$8,000 - $20,000/month'
      },
      caseStudy: {
        title: 'Enterprise Cloud Migration',
        challenge: 'Manufacturing company needed to migrate legacy systems to cloud while maintaining 99.9% uptime.',
        solution: 'Implemented phased migration strategy using AWS services with zero-downtime deployment.',
        results: '60% reduction in infrastructure costs, 99.99% uptime achieved, 50% faster deployment cycles.'
      },
      process: ['Assessment', 'Strategy Planning', 'Migration', 'Optimization', 'Monitoring', 'Support']
    },
    {
      id: 'data-analytics',
      icon: 'BarChart3',
      title: 'Data Analytics',
      description: 'Transform your data into actionable insights with advanced analytics and visualization tools.',
      shortDesc: 'Advanced analytics and data visualization solutions',
      benefits: ['Real-time Insights', 'Predictive Analytics', 'Custom Dashboards', 'Data Integration'],
      technologies: ['Python', 'R', 'Tableau', 'Power BI'],
      pricing: {
        basic: '$3,000 - $8,000',
        standard: '$8,000 - $20,000',
        premium: '$20,000 - $50,000'
      },
      caseStudy: {
        title: 'Retail Analytics Dashboard',
        challenge: 'Retail chain needed unified view of sales data across 200+ stores with real-time reporting.',
        solution: 'Built comprehensive analytics platform with automated data pipelines and interactive dashboards.',
        results: '25% increase in sales efficiency, 40% faster decision making, $2M cost savings identified.'
      },
      process: ['Data Audit', 'Architecture Design', 'ETL Development', 'Dashboard Creation', 'Training', 'Optimization']
    },
    {
      id: 'cybersecurity',
      icon: 'Shield',
      title: 'Cybersecurity',
      description: 'Comprehensive security solutions to protect your digital assets and ensure compliance.',
      shortDesc: 'Comprehensive security and compliance solutions',
      benefits: ['Threat Detection', 'Compliance Management', 'Security Audits', '24/7 Monitoring'],
      technologies: ['SIEM Tools', 'Penetration Testing', 'Encryption', 'Firewall Management'],
      pricing: {
        basic: '$2,000 - $5,000/month',
        standard: '$5,000 - $12,000/month',
        premium: '$12,000 - $30,000/month'
      },
      caseStudy: {
        title: 'Financial Services Security Overhaul',
        challenge: 'Financial institution needed comprehensive security upgrade to meet regulatory requirements.',
        solution: 'Implemented multi-layered security architecture with advanced threat detection and response systems.',
        results: 'Zero security incidents in 18 months, full regulatory compliance, 90% reduction in false positives.'
      },
      process: ['Security Assessment', 'Risk Analysis', 'Implementation', 'Testing', 'Training', 'Monitoring']
    },
    {
      id: 'ai-ml',
      icon: 'Brain',
      title: 'AI & Machine Learning',
      description: 'Intelligent solutions powered by artificial intelligence and machine learning technologies.',
      shortDesc: 'AI-powered intelligent business solutions',
      benefits: ['Process Automation', 'Predictive Modeling', 'Natural Language Processing', 'Computer Vision'],
      technologies: ['TensorFlow', 'PyTorch', 'OpenAI', 'Scikit-learn'],
      pricing: {
        basic: '$10,000 - $25,000',
        standard: '$25,000 - $75,000',
        premium: '$75,000 - $200,000'
      },
      caseStudy: {
        title: 'Customer Service Chatbot',
        challenge: 'E-commerce company needed to reduce customer service costs while improving response times.',
        solution: 'Developed AI-powered chatbot with natural language processing and integration with existing systems.',
        results: '70% reduction in support tickets, 24/7 customer support, 95% customer satisfaction rate.'
      },
      process: ['Problem Definition', 'Data Collection', 'Model Development', 'Training', 'Deployment', 'Optimization']
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Discovery & Analysis',
      description: 'We begin by understanding your business requirements, challenges, and objectives through comprehensive analysis.',
      deliverables: ['Requirements Document', 'Technical Specification', 'Project Timeline']
    },
    {
      step: 2,
      title: 'Strategy & Planning',
      description: 'Develop a detailed project strategy with clear milestones, resource allocation, and risk assessment.',
      deliverables: ['Project Plan', 'Architecture Design', 'Resource Allocation']
    },
    {
      step: 3,
      title: 'Design & Prototyping',
      description: 'Create user-centered designs and interactive prototypes to validate concepts before development.',
      deliverables: ['UI/UX Designs', 'Interactive Prototype', 'Design System']
    },
    {
      step: 4,
      title: 'Development & Testing',
      description: 'Agile development process with continuous testing to ensure quality and performance standards.',
      deliverables: ['Working Software', 'Test Reports', 'Documentation']
    },
    {
      step: 5,
      title: 'Deployment & Launch',
      description: 'Seamless deployment with comprehensive testing in production environment and go-live support.',
      deliverables: ['Production Deployment', 'Launch Support', 'Performance Monitoring']
    },
    {
      step: 6,
      title: 'Support & Maintenance',
      description: 'Ongoing support, maintenance, and optimization to ensure continued success and growth.',
      deliverables: ['24/7 Support', 'Regular Updates', 'Performance Reports']
    }
  ];

  const metrics = [
    { label: 'Projects Completed', value: '32+', icon: 'CheckCircle' },
    { label: 'Client Satisfaction', value: '98%', icon: 'Heart' },
    { label: 'Years of Experience', value: '3+', icon: 'Calendar' },
    { label: 'Team Members', value: '10+', icon: 'Users' }
  ];

  // const certifications = [
  //   { name: 'AWS Certified Solutions Architect', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=60&fit=crop' },
  //   { name: 'Microsoft Azure Certified', logo: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=100&h=60&fit=crop' },
  //   { name: 'Google Cloud Professional', logo: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=100&h=60&fit=crop' },
  //   { name: 'ISO 27001 Certified', logo: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=100&h=60&fit=crop' }
  // ];

  // const partners = [
  //   { name: 'Amazon Web Services', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop' },
  //   { name: 'Microsoft Azure', logo: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=120&h=60&fit=crop' },
  //   { name: 'Google Cloud', logo: 'https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=120&h=60&fit=crop' },
  //   { name: 'MongoDB', logo: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=120&h=60&fit=crop' },
  //   { name: 'Docker', logo: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=120&h=60&fit=crop' },
  //   { name: 'React', logo: 'https://images.unsplash.com/photo-1633356122102-3fe601e05bd2?w=120&h=60&fit=crop' }
  // ];

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setIsModalOpen(true);
  };

  const handleComparisonToggle = (serviceId) => {
    setComparisonServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else if (prev.length < 3) {
        return [...prev, serviceId];
      }
      return prev;
    });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['services', 'process', 'metrics', 'contact'];
      // Adjust scroll position to activate section slightly higher up
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    // Add CSS class for page load animation
    document.body.classList.add('page-loaded');

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      {/* <div className="bg-surface border-b border-border mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/homepage" className="text-text-secondary hover:text-primary nav-transition">
              Home
            </Link>
            <Icon name="ChevronRight" size={16} className="text-text-secondary" />
            <span className="text-primary font-medium">Services</span>
          </nav>
        </div>
      </div> */}

      {/* Hero Section */}
      <motion.section
        className="bg-gradient-to-br from-primary-50 to-accent-50 py-16 lg:py-24"
        initial={{ opacity: 0, y: -50 }} // Initial state
        animate={{ opacity: 1, y: 0 }} // Animation state after component mounts
        transition={{ duration: 0.8 }} // Transition duration
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            <motion.h1
              className="text-4xl lg:text-6xl font-heading font-bold text-text-primary mb-6"
              variants={{ hidden: { opacity: 0, y: 50 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 20 } } }} // Use simpler variant for header
            >
              Our <span className="text-primary">Services</span>
            </motion.h1>
            <motion.p
              className="text-xl text-text-secondary mb-8 leading-relaxed"
              variants={{ hidden: { opacity: 0, y: 50 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 20, delay: 0.2 } } }}
            >
              Comprehensive IT solutions designed to transform your business and drive growth through innovative technology
            </motion.p>
            <motion.div
              className="flex flex-wrap justify-center gap-4 "
              variants={{ hidden: { opacity: 0, y: 50 }, show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 20, delay: 0.4 } } }}
            >
              <button
                onClick={() => scrollToSection('services')}
                className="btn-primary"
              >
                Explore Services
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="btn-secondary"
              >
                Get Quote
              </button>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Internal Navigation */}
      <div className="sticky top-14 bg-surface border-b border-border z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto py-3">
            {[
              { id: 'services', label: 'Services', icon: 'Settings' },
              { id: 'process', label: 'Our Process', icon: 'GitBranch' },
              // { id: 'metrics', label: 'Success Metrics', icon: 'TrendingUp' },
              { id: 'contact', label: 'Get Started', icon: 'MessageCircle' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-300 ease-in-out whitespace-nowrap ${activeSection === item.id
                  ? 'bg-primary text-white shadow-lg shadow-primary/50' : 'text-text-secondary hover:text-primary hover:bg-primary-50'
                  }`}
              >
                <Icon name={item.icon} size={16} />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Services Section */}
      <section id="services" className="py-16 lg:py-10" ref={servicesRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.8,
                ease: "easeOut"
              }
            }}
          >

            {/* Floating wrapper */}
            <motion.div
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 4,
                repeat: 0.5,
                ease: "easeInOut"
              }}
            >
              <h2
                className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4"
              >
                <motion.span
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: 0.2, duration: 0.7 }
                  }}
                >
                  What We Offer
                </motion.span>
              </h2>

              <motion.p
                className="text-lg text-text-secondary max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 15 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { delay: 0.4, duration: 0.7 }
                }}
              >
                From web development to AI solutions, we provide comprehensive IT
                services tailored to your business needs
              </motion.p>
            </motion.div>
          </motion.div>

          {/* Service Comparison Tool */}
          {comparisonServices.length > 0 && (
            <motion.div
              className="mb-12 p-6 bg-primary-50 rounded-lg border border-primary-100"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">
                  Service Comparison ({comparisonServices.length}/3)
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowComparison(true)}
                    className={`text-sm transition-all duration-300 ${comparisonServices.length < 2 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'btn-primary'}`}
                    disabled={comparisonServices.length < 2}
                  >
                    Compare Services
                  </button>
                  <button
                    onClick={() => setComparisonServices([])}
                    className="btn-secondary text-sm"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {comparisonServices.map(serviceId => {
                  const service = services.find(s => s.id === serviceId);
                  return (
                    <span
                      key={serviceId}
                      className="inline-flex items-center px-3 py-1 bg-primary text-white rounded-full text-sm transition-all duration-300"
                    >
                      {service?.title}
                      <button
                        onClick={() => handleComparisonToggle(serviceId)}
                        className="ml-2 hover:bg-primary-700 rounded-full p-0.5 transition-colors"
                      >
                        <Icon name="X" size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Services Grid (Now using AnimatedServiceCard for per-item scroll effect) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {services.map((service, index) => (
              <AnimatedServiceCard
                key={service.id}
                service={service}
                index={index} // Pass index for directional animation
                onServiceClick={handleServiceClick}
                onComparisonToggle={handleComparisonToggle}
                isInComparison={comparisonServices.includes(service.id)}
                comparisonDisabled={comparisonServices.length >= 3 && !comparisonServices.includes(service.id)}
              />
            ))}
          </div>

          {/* Success Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-1" ref={metricsRef}>
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 50 }}
                animate={isMetricsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse-once"> {/* Pulse effect for icon container */}
                  <Icon name={metric.icon} size={24} color="var(--color-primary)" />
                </div>
                <div className="text-3xl font-bold text-text-primary mb-2">{metric.value}</div>
                <div className="text-text-secondary">{metric.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section id="process" className="py-16 lg:py-8 bg-secondary-50" ref={processRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
              Our Process
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              A proven methodology that ensures successful project delivery from concept to completion
            </p>



          </motion.div>
          <ProcessTimeline steps={processSteps} isVisible={isProcessInView} />
        </div>
      </section>

      {/* Certifications & Partners */}
      {/* <section id="metrics" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-6">
              Trusted & Certified
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Our expertise is validated by industry certifications and partnerships with leading technology providers
            </p>
          </div>

                {/* Certifications 
          <div className="mb-16">
            <h3 className="text-xl font-semibold text-text-primary text-center mb-8">Certifications</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {certifications.map((cert, index) => (
                <div key={index} className="text-center">
                  <div className="bg-surface rounded-lg p-6 card-shadow hover:card-shadow-lg nav-transition">
                    <Image
                      src={cert.logo}
                      alt={cert.name}
                      className="w-20 h-12 object-contain mx-auto mb-4"
                    />
                    <p className="text-sm font-medium text-text-primary">{cert.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

         {/* Technology Partners  
          <div>
            <h3 className="text-xl font-semibold text-text-primary text-center mb-8">Technology Partners</h3>
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-6">
              {partners.map((partner, index) => (
                <div key={index} className="text-center">
                  <div className="bg-surface rounded-lg p-4 card-shadow hover:card-shadow-lg nav-transition">
                    <Image
                      src={partner.logo}
                      alt={partner.name}
                      className="w-full h-8 object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          </div>
      </section> */}

      {/* Contact Section */}
      <section id="contact" className="py-16 lg:py-10 bg-gradient-to-br from-primary-50 to-accent-50" ref={contactRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={isContactInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Let's discuss your project requirements and find the perfect solution for your business
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={isContactInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ContactForm services={services} />
          </motion.div>
        </div>
      </section>

      {/* Service Modal */}
      {isModalOpen && selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedService(null);
          }}
        />
      )}

      {/* Service Comparison Modal */}
      {showComparison && comparisonServices.length >= 2 && (
        <ServiceComparison
          services={services.filter(s => comparisonServices.includes(s.id))}
          onClose={() => setShowComparison(false)}
        />
      )}
      <Footer />
    </div>
  );
};

export default Services;